import { useState, useEffect } from "react";
import "@/App.css";
import { Button } from "@/components/ui/button";
import { Zap, Upload, Download, X, Moon, Sun } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { RenderCMSContent } from "@/components/CMSRenderer";
import { AdPlacement, AdContainer, ResponsiveAd, StickyMobileAd, DesktopOnlyAd } from "@/components/AdPlacement";

import {
  useImageCompression,
  formatBytes,
  Header,
  ProgressBar,
  UploadZone,
  StatsPanel,
  QuickCompressPanel,
  CompressionSettings,
  ImageCard
} from "@/components/compressor";

function App({ embedded = false }) {
  const {
    // State
    images,
    quality,
    format,
    maxWidth,
    maxHeight,
    compressionPreset,
    targetPercentage,
    removeExif,
    exifOptions,
    isDragging,
    isProcessing,
    processingImageId,
    compressionProgress,
    comparisonMode,
    
    // Refs
    fileInputRef,
    addMoreInputRef,
    
    // Setters
    setQuality,
    setFormat,
    setMaxWidth,
    setMaxHeight,
    setCompressionPreset,
    setTargetPercentage,
    setRemoveExif,
    setExifOptions,
    setIsDragging,
    
    // Actions
    processMultipleImages,
    updateImageSettings,
    handleDownloadSingle,
    handleDownloadAll,
    handleClearAll,
    handleRemoveImage,
    handleQuickCompress,
    handlePercentageCompress,
    toggleComparisonMode,
    calculateTotalStats,
  } = useImageCompression();

  const [homepageContent, setHomepageContent] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Inject Google Analytics script
  const injectGoogleAnalytics = (gaId) => {
    if (!gaId || document.querySelector('script[src*="googletagmanager"]')) return;
    
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(script);
    
    const initScript = document.createElement('script');
    initScript.textContent = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${gaId}');
    `;
    document.head.appendChild(initScript);
  };

  // Fetch site settings and inject GA + Search Console
  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/settings`);
        if (response.ok) {
          const data = await response.json();
          if (data.google_analytics_id) {
            injectGoogleAnalytics(data.google_analytics_id);
          }
          if (data.google_search_console_verification && !document.querySelector('meta[name="google-site-verification"]')) {
            const meta = document.createElement('meta');
            meta.name = 'google-site-verification';
            meta.content = data.google_search_console_verification;
            document.head.appendChild(meta);
          }
        }
      } catch (error) {
        console.log('Failed to load site settings');
      }
    };
    
    if (!embedded) {
      fetchSiteSettings();
    }
  }, [embedded, API_URL]);

  // Fetch homepage CMS content
  useEffect(() => {
    const fetchHomepageContent = async () => {
      try {
        const response = await fetch(`${API_URL}/api/pages/slug/homepage`);
        if (response.ok) {
          const data = await response.json();
          setHomepageContent(data);
        }
      } catch (error) {
        console.log('No homepage CMS content found');
      }
    };

    if (!embedded) {
      fetchHomepageContent();
    }
  }, [embedded, API_URL]);

  // Apply SEO settings from homepage CMS content
  useEffect(() => {
    if (!embedded && homepageContent?.seo) {
      const seo = homepageContent.seo;
      
      if (seo.meta_title) {
        document.title = seo.meta_title;
      }
      
      const setMetaTag = (name, content, isProperty = false) => {
        if (!content) return;
        const attr = isProperty ? 'property' : 'name';
        let meta = document.querySelector(`meta[${attr}="${name}"]`);
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute(attr, name);
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', content);
      };
      
      setMetaTag('description', seo.meta_description);
      setMetaTag('keywords', seo.meta_keywords);
      setMetaTag('og:title', seo.og_title || seo.meta_title, true);
      setMetaTag('og:description', seo.og_description || seo.meta_description, true);
      setMetaTag('og:image', seo.og_image, true);
      setMetaTag('og:type', 'website', true);
      
      if (seo.canonical_url) {
        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) {
          canonical = document.createElement('link');
          canonical.setAttribute('rel', 'canonical');
          document.head.appendChild(canonical);
        }
        canonical.setAttribute('href', seo.canonical_url);
      }
      
      if (seo.no_index || seo.no_follow) {
        const robotsContent = [
          seo.no_index ? 'noindex' : 'index',
          seo.no_follow ? 'nofollow' : 'follow'
        ].join(', ');
        setMetaTag('robots', robotsContent);
      }
    }
  }, [embedded, homepageContent]);

  // Apply dark mode class to document
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(prev => !prev);
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith("image/") || 
      file.name.toLowerCase().endsWith('.svg') ||
      file.name.toLowerCase().endsWith('.gif') ||
      file.name.toLowerCase().endsWith('.heic') ||
      file.name.toLowerCase().endsWith('.heif')
    );
    if (files.length > 0) {
      processMultipleImages(files);
    } else {
      toast.error("Please drop valid image files");
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      processMultipleImages(files);
    }
    e.target.value = '';
  };

  const handleAddMoreImages = () => {
    addMoreInputRef.current?.click();
  };

  // Handle paste event for clipboard images
  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    
    const imageFiles = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/') || items[i].type === 'image/svg+xml' || items[i].type === 'image/gif') {
        const file = items[i].getAsFile();
        if (file) {
          const extension = file.type.split('/')[1] || 'png';
          const namedFile = new File([file], `pasted-image-${Date.now()}.${extension}`, { type: file.type });
          imageFiles.push(namedFile);
        }
      }
    }
    
    if (imageFiles.length > 0) {
      e.preventDefault();
      processMultipleImages(imageFiles);
      toast.success(`${imageFiles.length} image${imageFiles.length > 1 ? 's' : ''} pasted!`);
    }
  };

  // Add paste event listener
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    document.addEventListener('paste', handlePaste);
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, []);

  const stats = calculateTotalStats();

  // Embedded mode - simplified version
  if (embedded) {
    return (
      <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
        <Toaster position="top-center" theme={darkMode ? "dark" : "light"} />
        
        {/* Embedded Header */}
        <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold text-slate-900 dark:text-white">Image Compressor</span>
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={toggleDarkMode}
            className="rounded-full w-8 h-8 border-slate-200 dark:border-slate-600"
          >
            {darkMode ? <Sun className="w-4 h-4 text-yellow-500" /> : <Moon className="w-4 h-4 text-slate-600" />}
          </Button>
        </div>

        {/* Upload Zone */}
        {images.length === 0 ? (
          <div
            data-testid="upload-zone"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`h-48 border-2 border-dashed rounded-lg cursor-pointer ${
              isDragging ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20" : "border-slate-300 dark:border-slate-600"
            } bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 flex flex-col items-center justify-center gap-2 transition-colors`}
          >
            <Upload className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            <p className="text-sm text-slate-600 dark:text-slate-400">Drop images or click to upload</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.heic,.heif,.svg,.gif"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-2 text-center text-sm">
              <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded">
                <p className="text-slate-500 dark:text-slate-400">Original</p>
                <p className="font-semibold text-slate-900 dark:text-white">{formatBytes(stats.totalOriginal)}</p>
              </div>
              <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded">
                <p className="text-slate-500 dark:text-slate-400">Compressed</p>
                <p className="font-semibold text-blue-600">{formatBytes(stats.totalCompressed)}</p>
              </div>
              <div className="p-2 bg-emerald-50 dark:bg-emerald-900/30 rounded">
                <p className="text-emerald-600 dark:text-emerald-400">Saved</p>
                <p className="font-semibold text-emerald-600 dark:text-emerald-400">{stats.percentage}%</p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={() => images.length === 1 ? handleDownloadSingle(images[0]) : handleDownloadAll()}
                className="bg-blue-600 hover:bg-blue-700"
                size="sm"
              >
                <Download className="w-4 h-4 mr-1" />
                Download
              </Button>
              <Button onClick={handleAddMoreImages} variant="outline" size="sm">
                <Upload className="w-4 h-4 mr-1" />
                Add More
              </Button>
              <Button onClick={handleClearAll} variant="outline" size="sm" className="text-red-600 border-red-200">
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            </div>

            {/* Images preview */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {images.slice(0, 6).map((image) => (
                <div key={image.id} className="relative aspect-square bg-slate-100 dark:bg-slate-700 rounded overflow-hidden">
                  <img src={image.compressed?.src || image.originalSrc} alt={image.name} className="w-full h-full object-cover" />
                  {image.wasConverted && (
                    <span className="absolute top-1 left-1 text-[9px] bg-purple-500 text-white px-1 rounded">
                      HEIC
                    </span>
                  )}
                  {image.compressed && (
                    <span className="absolute bottom-1 right-1 text-xs bg-emerald-500 text-white px-1 rounded">
                      {formatBytes(image.compressed.size)}
                    </span>
                  )}
                </div>
              ))}
            </div>
            {images.length > 6 && (
              <p className="text-sm text-slate-500 text-center">+{images.length - 6} more images</p>
            )}
          </div>
        )}

        {/* Hidden inputs */}
        <input ref={addMoreInputRef} type="file" accept="image/*,.heic,.heif,.svg,.gif" multiple onChange={handleFileSelect} className="hidden" />
      </div>
    );
  }

  // Main full-page mode
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 grid-pattern transition-colors duration-300">
      <Toaster position="top-center" theme={darkMode ? "dark" : "light"} />
      
      {/* Header */}
      <Header darkMode={darkMode} onToggleDarkMode={toggleDarkMode} />

      {/* Ad Placement - Above the Fold (Below Header) */}
      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      {/* Progress Bar */}
      <ProgressBar 
        isProcessing={isProcessing}
        processingImageId={processingImageId}
        compressionProgress={compressionProgress}
      />

      <main className="container mx-auto px-4 py-8 md:py-12">
        {/* Upload Zone */}
        {images.length === 0 && (
          <UploadZone
            isDragging={isDragging}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            fileInputRef={fileInputRef}
            onFileSelect={handleFileSelect}
          />
        )}

        {/* Workspace */}
        {images.length > 0 && (
          <div className="space-y-6">
            {/* Stats Panel */}
            <StatsPanel
              stats={stats}
              images={images}
              isProcessing={isProcessing}
              comparisonMode={comparisonMode}
              onDownloadSingle={handleDownloadSingle}
              onDownloadAll={handleDownloadAll}
              onClearAll={handleClearAll}
              onAddMore={handleAddMoreImages}
            />

            {/* Quick Compress Panel */}
            {!comparisonMode && (
              <QuickCompressPanel
                targetPercentage={targetPercentage}
                setTargetPercentage={setTargetPercentage}
                onQuickCompress={handleQuickCompress}
                onPercentageCompress={handlePercentageCompress}
                isProcessing={isProcessing}
                hasImages={images.length > 0}
              />
            )}

            {/* Compression Settings */}
            <CompressionSettings
              quality={quality}
              setQuality={setQuality}
              format={format}
              setFormat={setFormat}
              maxWidth={maxWidth}
              setMaxWidth={setMaxWidth}
              maxHeight={maxHeight}
              setMaxHeight={setMaxHeight}
              compressionPreset={compressionPreset}
              setCompressionPreset={setCompressionPreset}
              removeExif={removeExif}
              setRemoveExif={setRemoveExif}
              exifOptions={exifOptions}
              setExifOptions={setExifOptions}
              comparisonMode={comparisonMode}
              onToggleComparisonMode={toggleComparisonMode}
            />

            {/* Ad Placement - Above Results (336×280 - Best Money Position) */}
            <DesktopOnlyAd slot="above-results" size="large-rectangle" className="my-6" />

            {/* Images Grid */}
            <div className="space-y-8">
              {images.map((image) => (
                <ImageCard
                  key={image.id}
                  image={image}
                  isProcessing={isProcessing}
                  processingImageId={processingImageId}
                  comparisonMode={comparisonMode}
                  onDownload={handleDownloadSingle}
                  onRemove={handleRemoveImage}
                  onUpdateSettings={updateImageSettings}
                  onQuickCompress={handleQuickCompress}
                  onPercentageCompress={handlePercentageCompress}
                />
              ))}
            </div>
          </div>
        )}

        {/* Hidden file inputs */}
        <input 
          ref={addMoreInputRef} 
          type="file" 
          accept="image/*,.heic,.heif,.svg,.gif" 
          multiple 
          onChange={handleFileSelect} 
          className="hidden" 
        />

        {/* Ad Placement - After Main Content (Responsive) */}
        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-content" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      {/* CMS Content below the tool */}
      {homepageContent?.content && (
        <div className="bg-slate-50 dark:bg-slate-900 py-12">
          <div className="container mx-auto px-4">
            <RenderCMSContent content={homepageContent.content} />
          </div>
        </div>
      )}

      {/* Ad Placement - Before Footer (Responsive) */}
      <AdContainer className="my-8">
        <ResponsiveAd slot="before-footer" mobileSize="mobile-rectangle" desktopSize="rectangle" />
      </AdContainer>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12 transition-colors duration-300">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>

      {/* Sticky Mobile Ad - Fixed at bottom on mobile only */}
      <StickyMobileAd slot="sticky-footer" />
    </div>
  );
}

export default App;
